import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { AlertMessage } from '@/components/ui/AlertMessage';
import { Loader2 } from 'lucide-react';
import type { CreditPlanFormData, CreditPlan } from '../hooks/useCreditPlans';

interface CreditPlanFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isEdit: boolean;
  editingPlan: CreditPlan | null;
  formData: CreditPlanFormData;
  onFormDataChange: (data: CreditPlanFormData) => void;
  error: string;
  loading: boolean;
  onSubmit: () => void;
  onClose: () => void;
  onReset?: () => void;  // "원래대로" 버튼 클릭 시 호출
  initialFormData?: CreditPlanFormData | null;  // 수정 시작 시 초기값
}

export const CreditPlanFormDialog: React.FC<CreditPlanFormDialogProps> = ({
  open,
  onOpenChange,
  isEdit,
  editingPlan: _editingPlan,
  formData,
  onFormDataChange,
  error,
  loading,
  onSubmit,
  onClose,
  onReset,
  initialFormData,
}) => {
  // 숫자와 소수점만 입력 허용하는 핸들러 (소수점 한 자리까지만)
  const handleNumericInput = (field: 'monthly_base' | 'credit_per_call', value: string) => {
    // 빈 문자열 허용
    if (value === '') {
      onFormDataChange({ ...formData, [field]: '' });
      return;
    }
    
    // 숫자, 소수점, 음수 부호만 허용 (소수점은 하나만, 소수점 한 자리까지만)
    const numericRegex = /^-?\d*\.?\d{0,1}$/;
    if (numericRegex.test(value)) {
      onFormDataChange({ ...formData, [field]: value });
    }
  };
  const handleBillingTypeChange = (billingType: string) => {
    let next = { ...formData, billing_type: billingType as CreditPlanFormData['billing_type'] };
    if (billingType === 'free') {
      next = { ...next, monthly_base: '0', credit_per_call: '0' };
    } else if (billingType === 'usage') {
      next = { ...next, monthly_base: '0' };
    } else if (billingType === 'flat') {
      next = { ...next, credit_per_call: '0' };
    } else if (billingType === 'base_plus_usage') {
      next = {
        ...next,
        monthly_base: next.monthly_base || '1',
        credit_per_call: next.credit_per_call || '1',
      };
    }
    onFormDataChange(next);
  };

  const isFree = formData.billing_type === 'free';
  const isUsage = formData.billing_type === 'usage';
  const isFlat = formData.billing_type === 'flat';
  const isBasePlus = formData.billing_type === 'base_plus_usage';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" onClose={onClose}>
        <DialogHeader>
          <DialogTitle>{isEdit ? '크레딧 플랜 수정' : '새 크레딧 플랜 생성'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {error && (
            <AlertMessage
              type="error"
              message={error}
              onClose={() => {}} // 부모 컴포넌트에서 error 관리
            />
          )}
          <div>
            <Label>플랜 이름 *</Label>
            <Input
              value={formData.plan_name}
              onChange={(e) => onFormDataChange({ ...formData, plan_name: e.target.value })}
              placeholder="예: 기본 플랜, 프리미엄 플랜"
            />
          </div>

          <div>
            <Label>플랜 유형 *</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <Button
                type="button"
                variant={isFree ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleBillingTypeChange('free')}
              >
                1. 완전 무료
              </Button>
              <Button
                type="button"
                variant={isUsage ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleBillingTypeChange('usage')}
              >
                2. 사용량 플랜
              </Button>
              <Button
                type="button"
                variant={isFlat ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleBillingTypeChange('flat')}
              >
                3. 월정액 플랜
              </Button>
              <Button
                type="button"
                variant={isBasePlus ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleBillingTypeChange('base_plus_usage')}
              >
                4. 월 + 사용량 플랜
              </Button>
            </div>
          </div>

          <div>
            <Label>
              월 기본 크레딧
              {isFree || isUsage ? ' (0 고정)' : isFlat || isBasePlus ? ' (1 이상 필수)' : ''}
            </Label>
            <Input
              type="text"
              value={formData.monthly_base}
              onChange={(e) => handleNumericInput('monthly_base', e.target.value)}
              placeholder={isFree || isUsage ? '0' : '예: 5, 10.5, 100'}
              disabled={isFree || isUsage}
            />
          </div>

          <div>
            <Label>
              회당 사용 크레딧
              {isFree || isFlat ? ' (0 고정)' : isUsage || isBasePlus ? ' (1 이상 필수)' : ''}
            </Label>
            <Input
              type="text"
              value={formData.credit_per_call}
              onChange={(e) => handleNumericInput('credit_per_call', e.target.value)}
              placeholder={isFree || isFlat ? '0' : '예: 5, 10.5'}
              disabled={isFree || isFlat}
            />
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => onFormDataChange({ ...formData, is_active: e.target.checked })}
            />
            <Label htmlFor="is_active">활성화</Label>
          </div>
        </div>
        
        <DialogFooter className="pt-4 border-t mt-4">
          <div className="flex justify-between w-full">
            <div>
              {isEdit && onReset && initialFormData && (
                <Button type="button" variant="outline" onClick={onReset}>
                  원래대로
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                닫기
              </Button>
              <Button
                type="button"
                onClick={onSubmit}
                disabled={loading}
              >
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {isEdit ? '저장' : '생성'}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

